import { Directive, effect, signal, Signal, WritableSignal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { AgGridDefaults } from '@app/v2/utils/ag-grid/ag-grid-defaults';
import { LocalStorageAgGridHandler } from '@app/v2/utils/ag-grid/local-storage-ag-grid.utils';
import { effectAllDefined } from '@app/v2/utils/signal-utils';
import {
    ColDef,
    Column,
    GridApi,
    GridOptions,
    GridState,
    RowClickedEvent,
} from '@medpacesoftwaredevelopment/designsystem/lib/components/ag-grid';

/**
 * Holds the shared ag-grid wiring (quick filter, row data binding and local storage state)
 * for site lists. Derived components provide the data source, columns and row click behavior.
 */
@Directive()
export abstract class BaseSitesListComponent<TRow, TPrefilter = undefined> {
    protected abstract readonly gridStateStorageKey: string;
    protected abstract readonly rows: Signal<TRow[] | undefined>;
    protected abstract readonly rowsLoading: Signal<boolean>;

    /** Filter values persisted in local storage next to the grid state. */
    protected readonly prefilter: Signal<TPrefilter | undefined> = signal(undefined);

    public abstract readonly colDefs: ColDef<TRow>[];

    protected readonly initialStateLoaded: WritableSignal<boolean> = signal(false);
    private readonly gridColumns: WritableSignal<Column<any>[] | undefined> = signal(undefined);
    private readonly dataReady: WritableSignal<boolean> = signal(false);
    protected readonly gridState: WritableSignal<GridState | undefined> = signal(undefined);

    public readonly searchControl = new FormControl<string | null>(null);

    private readonly quickFilter: Signal<string | null | undefined> = toSignal(this.searchControl.valueChanges);

    public readonly gridApi: WritableSignal<GridApi | undefined> = signal(undefined);

    public readonly gridOptions: GridOptions = {
        includeHiddenColumnsInQuickFilter: true,
        sideBar: {
            ...AgGridDefaults.defaultSidebar,
        },
        onStateUpdated: (params) => this.gridState.set(params.state),
        onGridColumnsChanged: (eventCols) => {
            const columns = eventCols.api.getColumns();
            this.gridColumns.set(columns ?? undefined);
        },
        onFirstDataRendered: () => this.dataReady.set(true),
    };

    public readonly defaultColDef: ColDef = {
        ...AgGridDefaults.defaultColDef,
    };

    constructor() {
        // set quick filter in ag-grid from form control
        effect(() => {
            const gridApi = this.gridApi();
            if (!gridApi) return;

            gridApi.setGridOption('quickFilterText', this.quickFilter() ?? undefined);
        });

        // set data in ag-grid
        effectAllDefined({ gridApi: this.gridApi }, ({ gridApi }) => {
            const isLoading = this.rowsLoading();
            gridApi.setGridOption('loading', isLoading);

            if (isLoading) {
                return;
            }

            const data = this.rows();

            if (!data) {
                return;
            }

            gridApi.setGridOption('rowData', data);
        });

        // set local storage state from form control values
        effectAllDefined(
            {
                gridState: this.gridState,
                initialStateLoaded: this.initialStateLoaded,
            },
            (values) => {
                if (!values.initialStateLoaded) {
                    return;
                }

                LocalStorageAgGridHandler.saveToLocalStorage(
                    this.gridStateStorageKey,
                    values.gridState,
                    this.prefilter()
                );
            }
        );

        // set local storage grid state into ag-grid
        effectAllDefined(
            {
                initialStateLoaded: this.initialStateLoaded,
                gridApi: this.gridApi,
                gridColumns: this.gridColumns,
                dataReady: this.dataReady,
            },
            (values) => {
                const initialStateLoaded = values.initialStateLoaded;
                const gridApi = values.gridApi;
                const gridColumns = values.gridColumns;
                const dataReady = values.dataReady;

                if (initialStateLoaded || !gridApi || !gridColumns || !dataReady) {
                    return;
                }

                const state = LocalStorageAgGridHandler.getFromLocalStorage<TPrefilter>(this.gridStateStorageKey);

                const gridState = state?.state;

                if (!gridState) {
                    this.initialStateLoaded.set(true);
                    return;
                }

                if (!this.prepareGridState(gridState)) {
                    return;
                }

                gridApi.setState(gridState);
                this.initialStateLoaded.set(true);
            }
        );
    }

    /**
     * Hook to adjust the grid state restored from local storage before it is applied to ag-grid.
     * Return false to postpone applying the state; signals read here re-trigger the restore.
     */
    protected prepareGridState(_gridState: GridState): boolean {
        return true;
    }

    public abstract rowClick(row: RowClickedEvent<TRow>): void;
}
